import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function FriendsInvite() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | accepting | done | error
  const [friend, setFriend] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Stash the invite code so we can redirect back after sign-up
      try { sessionStorage.setItem("ff_pending_invite", code); } catch { /* ignore */ }
      nav("/login?next=" + encodeURIComponent(`/i/${code}`));
    }
  }, [loading, user, code, nav]);

  const accept = async () => {
    setStatus("accepting");
    try {
      const { data } = await api.post(`/friends/accept-link/${code}`);
      setFriend(data.friend);
      setStatus("done");
      toast.success(data.already_friends ? "Already friends!" : "Friend added 🎉");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not accept invite");
      setStatus("error");
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="friend-invite-page">
      <div className="ff-card p-8 md:p-10 max-w-md w-full text-center">
        {status === "idle" && (
          <>
            <div className="text-5xl">👋</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              You&apos;ve been invited!
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">
              Tap below to connect and start sharing recipes. Both of you need Pro to be friends.
            </p>
            {!user.is_pro && (
              <div className="mt-4 p-3 rounded-xl bg-[#FFF4E5] text-[#8A5C12] text-xs">
                You&apos;ll need Shop, Cal & Meal Pro before you can accept. <Link to="/app/billing" className="font-medium underline">See pricing</Link>
              </div>
            )}
            <button
              onClick={accept}
              disabled={!user.is_pro}
              data-testid="accept-invite-btn"
              className="ff-btn-primary text-sm mt-5 disabled:opacity-60 w-full"
            >
              Accept invite
            </button>
          </>
        )}
        {status === "accepting" && <div className="text-[#5C6B61]">Connecting…</div>}
        {status === "done" && (
          <>
            <div className="text-5xl">🎉</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              You&apos;re friends with {friend?.name || friend?.email}
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">Now you can share recipes back and forth.</p>
            <Link to="/app/friends" className="ff-btn-primary text-sm inline-block mt-5">Open friends</Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="text-4xl">😕</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Invite couldn&apos;t be claimed
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">{error}</p>
            <Link to="/app/friends" className="ff-btn-primary text-sm inline-block mt-5">Back to friends</Link>
          </>
        )}
      </div>
    </div>
  );
}
